<template>
  <!--
    Zvonček s upozorneniami: tiché ikonové tlačidlo s počtom neprečítaných, po kliknutí zoznam posledných upozornení.
    variant="sidebar" — päta tmavého ľavého panela (desktop), variant="topbar" — horná lišta na mobile.
    Klik na upozornenie otvorí záznam a označí ho ako prečítané.
  -->
  <!-- V ľavom paneli sa zoznam otvára vedľa panela (pri jeho päte), na mobile pod lištou -->
  <v-menu
    v-model="otvorene"
    :location="variant === 'topbar' ? 'bottom end' : 'end bottom'"
    :target="variant === 'topbar' ? undefined : 'parent'"
    :offset="variant === 'topbar' ? 6 : 12"
    :close-on-content-click="false"
  >
    <template #activator="{ props: menuProps }">
      <button
        type="button"
        class="zvoncek"
        :class="[`zvoncek--${variant}`, { 'is-open': otvorene }]"
        v-bind="menuProps"
        aria-haspopup="dialog"
        :aria-label="popis"
      >
        <v-icon icon="mdi-bell-outline" :size="variant === 'topbar' ? 22 : 18" />
        <span v-if="neprecitane" class="zvoncek__badge tabular-nums" aria-hidden="true">
          {{ neprecitane > 99 ? '99+' : neprecitane }}
        </span>
        <v-tooltip v-if="variant === 'sidebar'" activator="parent" :disabled="otvorene" :location="collapsed ? 'end' : 'top'">
          {{ popis }}
        </v-tooltip>
      </button>
    </template>

    <v-card class="notif-pop" role="dialog" aria-label="Upozornenia">
      <div class="notif-pop__head">
        <span class="notif-pop__title">Upozornenia</span>
        <v-btn v-if="neprecitane" variant="text" color="primary" size="small" @click="oznacitVsetko">
          Označiť všetko ako prečítané
        </v-btn>
      </div>

      <ul v-if="upozornenia.polozky.length" class="notif-pop__list">
        <li v-for="upozornenie in upozornenia.polozky" :key="upozornenie.id">
          <component
            :is="odkazUpozornenia(upozornenie) ? 'router-link' : 'button'"
            v-bind="odkazUpozornenia(upozornenie) ? { to: odkazUpozornenia(upozornenie) } : { type: 'button' }"
            class="notif-row list-row--clickable"
            :class="{ 'is-unread': !upozornenie.precitane }"
            @click="otvorit(upozornenie)"
          >
            <span class="notif-row__icon" aria-hidden="true">
              <v-icon :icon="ikonaUpozornenia(upozornenie.typ)" size="18" />
              <span v-if="!upozornenie.precitane" class="notif-row__dot"></span>
            </span>
            <span class="notif-row__body">
              <span v-if="!upozornenie.precitane" class="d-sr-only">Neprečítané: </span>
              <span class="notif-row__title">{{ upozornenie.titulok }}</span>
              <span v-if="upozornenie.text" class="notif-row__text">{{ upozornenie.text }}</span>
              <span class="notif-row__meta">
                <time :datetime="upozornenie.aktualizovane" :title="presnyCas(upozornenie.aktualizovane)">
                  {{ relativnyCas(upozornenie.aktualizovane) }}
                </time>
                <template v-if="opakovanieUpozornenia(upozornenie)"> · {{ opakovanieUpozornenia(upozornenie) }}</template>
              </span>
            </span>
          </component>
        </li>
      </ul>

      <div v-else-if="!upozornenia.nacitane" class="notif-pop__loading">
        <v-progress-circular indeterminate size="24" width="2" />
      </div>

      <div v-else class="empty-state notif-pop__empty">
        <v-icon icon="mdi-bell-outline" size="48" />
        <div class="text-body-1 font-weight-medium text-high-emphasis">Žiadne nové upozornenia</div>
        <div class="text-body-2">Komentáre, úlohy a požiadavky, ktoré sa vás týkajú, uvidíte tu.</div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { computed, defineComponent, ref, watch } from 'vue'
import { useNotificationStore } from '@/store/notification'
import { useNotifikacie } from '@/composables/useNotifikacie'

export default defineComponent({
  name: 'NotifikacieZvoncek',
  props: {
    /** 'sidebar' (tmavý ľavý panel) | 'topbar' (svetlá horná lišta na mobile) */
    variant: { type: String, default: 'sidebar' },
    /** Zbalený ľavý panel — popis tlačidla sa zobrazí vedľa neho */
    collapsed: { type: Boolean, default: false },
  },

  setup() {
    const notificationStore = useNotificationStore()
    const notifikacie = useNotifikacie()
    const { upozornenia, nacitajUpozornenia, precitat, precitatVsetko, pocetNeprecitanychText } = notifikacie
    const otvorene = ref(false)

    const neprecitane = computed(() => upozornenia.neprecitane)
    const popis = computed(() =>
      neprecitane.value ? `Upozornenia · ${pocetNeprecitanychText(neprecitane.value)}` : 'Upozornenia'
    )

    // Pri otvorení načítame čerstvý zoznam (inak sa obnovuje spolu s počtami v menu každých 30 s)
    watch(otvorene, (je) => {
      if (je) nacitajUpozornenia()
    })

    // Klik na upozornenie: odkaz otvorí záznam (router-link), tu len zatvoríme zoznam a označíme prečítané
    const otvorit = (upozornenie) => {
      otvorene.value = false
      precitat(upozornenie)
    }

    const oznacitVsetko = async () => {
      if (!(await precitatVsetko())) {
        notificationStore.show({ text: 'Upozornenia sa nepodarilo označiť ako prečítané.', color: 'error' })
      }
    }

    return {
      ...notifikacie,
      otvorene,
      neprecitane,
      popis,
      otvorit,
      oznacitVsetko,
    }
  },
})
</script>

<style lang="scss" scoped>
/* ---- Tlačidlo */
.zvoncek {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:focus-visible {
    outline: 2px solid var(--ruby-300);
    outline-offset: 0;
  }
}

/* V tmavom paneli rovnaké ako tlačidlo odhlásenia (.sidebar-icon-btn) */
.zvoncek--sidebar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--nav-muted);

  &:hover,
  &:focus-visible,
  &.is-open {
    background: var(--nav-hover);
    color: var(--brand-white);
  }
}

/* V hornej lište na mobile ako tlačidlo menu */
.zvoncek--topbar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: var(--text-primary);

  &:active,
  &.is-open {
    background: var(--surface-pearl);
  }
}

.zvoncek__badge {
  position: absolute;
  top: -2px;
  right: -4px;
  display: grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 9px;
  background: var(--brand-ruby);
  box-shadow: 0 0 0 2px var(--nav-bg);
  color: var(--brand-white);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;

  .zvoncek--topbar & {
    top: 2px;
    right: 1px;
    min-width: 18px;
    height: 18px;
    font-size: 10.5px;
    box-shadow: 0 0 0 2px var(--surface);
  }
}

/* ---- Zoznam upozornení (biela karta menu: polomer 14 a tieň pop sú globálne) */
.notif-pop {
  width: min(380px, calc(100vw - 24px));
  max-height: min(560px, calc(100vh - 24px));
  max-height: min(560px, calc(100dvh - 24px));
}

.notif-pop__head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 52px;
  padding: 8px 8px 8px 16px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--surface);
}

.notif-pop__title {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--text-primary);
}

.notif-pop__list {
  margin: 0;
  padding: 0;
  list-style: none;

  > li + li .notif-row {
    border-top: 1px solid var(--border-subtle);
  }
}

.notif-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  color: inherit;
  text-align: left;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--ruby-300);
    outline-offset: -2px;
  }
}

.notif-row__icon {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--surface-pearl);
  color: var(--text-secondary);
}

/* Neprečítané: Ruby bodka na ikone (ako odznak v zbalenom menu) */
.notif-row__dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--brand-ruby);
  box-shadow: 0 0 0 2px var(--surface);
}

.notif-row__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.notif-row__title,
.notif-row__text {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow-wrap: anywhere;
}

.notif-row__title {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.35;
  color: var(--text-secondary);

  .is-unread & {
    font-weight: 650;
    color: var(--text-primary);
  }
}

.notif-row__text {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--text-muted);
}

.notif-row__meta {
  margin-top: 1px;
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.notif-pop__loading {
  display: grid;
  place-items: center;
  padding: 40px 24px;
  color: var(--text-muted);
}

.notif-pop__empty {
  padding: 36px 24px 40px;

  .text-body-2 {
    max-width: 30ch;
  }
}
</style>
